import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  GuardResult,
  MaybeAsync,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthenticationService } from '@flexyoo/auth';
import {
  EmployeePlanInvitationDataAccessService,
  EmployeeSettingsService,
} from '@flexyoo/employee/data-access';
import { UserAgreementService } from '@flexyoo/employee/ui';
import { map, of, switchMap } from 'rxjs';
import { RoleEnum } from 'src/app/shared/models/general/general-enum';

@Injectable()
export class UserAgreementGuard {
  constructor(
    protected readonly router: Router,
    private readonly auth: AuthenticationService,
    private readonly userAgreementService: UserAgreementService,
    private readonly employeeSettingsService: EmployeeSettingsService,
    private readonly employeePlanInvitationService: EmployeePlanInvitationDataAccessService
  ) {}

  public canActivate(
    _: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): MaybeAsync<GuardResult> {
    const user = this.auth.getCurrentUser();

    if (!user) {
      this.auth.logout();
      return false;
    }

    if (user.Role === RoleEnum.Employee) {
      return this.employeeSettingsService.updateAndLoadEmployeeSettings(user.Id).pipe(
        switchMap(({ employeeSettings, tenantSettings }) => {
          if (!employeeSettings || !tenantSettings) {
            return of(false);
          }

          return this.employeePlanInvitationService
            .loadEmployeePlanInvitation(user.EmployeeId)
            .pipe(
              map((invitation) => {
                const userAgreementState = this.userAgreementService.checkUserAgreementState(
                  employeeSettings,
                  tenantSettings,
                  invitation
                );
                switch (userAgreementState) {
                  case 'to-be-uploaded': {
                    if (state.url.includes('employee-account')) {
                      return true;
                    }
                    this.router.navigate(['/employee-account']);
                    return false;
                  }
                  case 'sign-agreement-expired':
                  case 'under-validation':
                    this.router.navigate(['/login']);
                    return false;
                  case 'ok':
                    return true;
                }
              })
            );
        })
      );
    }

    return true;
  }
}
